import styled from 'styled-components';

import { TabsContainer as BaseTabsContainer } from 'components/Tabs/styles';

export const DetailContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

export const Section = styled.section`
  background: ${({ theme }) => theme.palette.common.white};
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.spacing(1)};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  min-height: ${({ theme }) => theme.spacing(7.5)};
  padding: ${({ theme }) => theme.spacing(1.5, 2.5)};
  border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.h5.fontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const SectionBody = styled.div`
  padding: ${({ theme }) => theme.spacing(2.5)};
`;

export const TabsContainer = styled(BaseTabsContainer)`
  flex: 1 0 auto;
`;

export const TabPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  gap: ${({ theme }) => theme.spacing(2.5)};
  width: 100%;
`;

export const MembersToolbar = styled.div`
  padding: ${({ theme }) => theme.spacing(2, 2.5)};
`;

// --- History tab ---

export const HistoryCard = styled(Section)`
  flex: 1 0 auto;
  overflow: hidden;
`;

export const HistoryBody = styled.div`
  padding: ${({ theme }) => theme.spacing(2, 2.5)};
`;

export const HistoryEmpty = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const InlineRule = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  vertical-align: middle;

  & > div {
    display: inline-flex;
  }
`;

export const HistoryBatchRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(1)};
  border-radius: ${({ theme }) => theme.spacing(1)};
  // The shared single row bakes ~16px of vertical air into its box via default <p> margins; a batch
  // row has none, so consecutive batches sat flush against each other. Match that rhythm.
  padding: ${({ theme }) => theme.spacing(1.5, 0)};
`;

export const HistoryBatchTime = styled.p`
  flex-shrink: 0;
  margin: 0;
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
`;

export const HistoryBatchDot = styled.span`
  flex-shrink: 0;
  width: 4px;
  height: 4px;
  margin-top: ${({ theme }) => theme.spacing(1)};
  border-radius: 50%;
  background: ${({ theme }) => theme.palette.grey[600]};
`;

export const HistoryBatch = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const HistoryBatchLine = styled.span`
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
  line-height: 20px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const HistoryBatchModalList = styled.div`
  max-height: 420px;
  overflow-y: auto;
`;

export const HistoryBatchName = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(0.375, 0)};
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
  line-height: 20px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const HistoryBatchLoading = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const HistoryBatchPartial = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(1, 0, 0)};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const HistoryBatchToggle = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  cursor: pointer;
`;

export const DetailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const DetailRow = styled.div`
  display: grid;
  grid-template-columns: ${({ theme }) => theme.spacing(20)} 1fr;
  align-items: start;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const DetailLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const DetailLabelWithHint = styled(DetailLabel)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};

  svg {
    font-size: ${({ theme }) => theme.typography.h5.fontSize};
    color: ${({ theme }) => theme.palette.text.disabled};
    cursor: help;
  }
`;

export const DetailValue = styled.span`
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
  color: ${({ theme }) => theme.palette.text.primary};
  white-space: pre-wrap;
`;

export const EditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(2.5)};
`;

export const InactiveBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => theme.spacing(1, 1, 1, 2)};
  border-radius: ${({ theme }) => theme.spacing(1)};
  border: 1px solid ${({ theme }) => theme.palette.warning.light};
  background: ${({ theme }) => theme.toast.warning.background};
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
`;

export const InactiveBannerMessage = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};

  svg {
    color: ${({ theme }) => theme.palette.warning.light};
  }
`;

export const MembersGridWrap = styled.div`
  & > div {
    border: none;
    border-radius: 0;
  }
`;

export const MemberNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const MemberRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  padding: ${({ theme }) => theme.spacing(0.5)};
  cursor: pointer;
  color: ${({ theme }) => theme.palette.text.secondary};

  &:hover {
    color: ${({ theme }) => theme.palette.error.main};
  }
`;

// --- Rule read row + rule-edit mode (dynamic groups) ---

export const RuleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

export const RuleEditActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(2.5)};
`;

export const RuleEditSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

// --- Shared modal impact ---

export const ImpactError = styled.div`
  color: ${({ theme }) => theme.palette.error.main};
  font-size: ${({ theme }) => theme.typography.subtitle2.fontSize};
`;

// --- Deactivate modal impact ---

export const DeactivateImpactBox = styled.div`
  margin: ${({ theme }) => theme.spacing(0.5, 0, 1)};
  padding: ${({ theme }) => theme.spacing(1.5, 2)};
  border-radius: 8px;
  background: ${({ theme }) => theme.palette.grey[100]};
`;

export const DeactivateImpactList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

export const DeactivateImpactHeading = styled.p`
  margin: ${({ theme }) => theme.spacing(0, 0, 1)};
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const DeactivateImpactRow = styled.li`
  margin: 0;
  padding: ${({ theme }) => theme.spacing(0.25, 0)};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 20px;
  color: ${({ theme }) => theme.palette.text.primary};
`;

export const DeactivateImpactEmpty = styled.p`
  margin: ${({ theme }) => theme.spacing(0.5, 0, 1)};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 20px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

export const DeactivateModalHelpText = styled.p`
  margin: ${({ theme }) => theme.spacing(2.5, 0, 1.5)};
  font-size: ${({ theme }) => theme.typography.body1.fontSize};
  line-height: 20px;
  color: ${({ theme }) => theme.palette.text.primary};
`;
